"use client";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/modal";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floating-label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import { reportScriptSchema } from "@/lib/validations/script";
import type * as z from "zod";

type ReportModalProps = {
  slug: string;
};

type ReportFormData = z.infer<typeof reportScriptSchema>;

export default function ReportModal({ slug }: ReportModalProps) {
  const [open, setOpen] = useState(false);
  const { user, accessToken } = useAuth();
  const { toast } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportFormData>({
    resolver: zodResolver(reportScriptSchema),
  });

  const onSubmit = async (data: ReportFormData) => {
    if (!user) {
      return toast({
        title: "Authentication required",
        description: "Please sign in to report",
        variant: "destructive",
      });
    }

    try {
      const body: any = {
        title: data.title,
        content: data.content,
      };

      if (data.image) {
        body.images = [data.image];
      }

      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/post/${slug}/report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        },
      ).then((res) => res.json());

      if (response.message !== "OK") {
        setOpen(false);

        return toast({
          title: "Error",
          description: response.message,
          variant: "destructive",
        });
      }

      toast({
        title: "Success",
        description: "Report submitted successfully",
      });

      setOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit report",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <button
        className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
        onClick={() => setOpen(true)}
      >
        Report
      </button>
      <Modal open={open} onOpenChange={setOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Report Script</ModalTitle>
          </ModalHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              <div className="flex flex-col gap-3">
                <div className="space-y-1">
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <FloatingLabelInput id="title" label="Title" {...field} />
                    )}
                  />
                  {errors.title && (
                    <p className="px-1 text-xs text-red-600">
                      {errors.title.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        id="content"
                        placeholder="Content"
                        className="placeholder:text-slate-500"
                        {...field}
                      />
                    )}
                  />
                  {errors.content && (
                    <p className="px-1 text-xs text-red-600">
                      {errors.content.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Controller
                    name="image"
                    control={control}
                    render={({ field }) => (
                      <FloatingLabelInput
                        id="image"
                        label="Image URL"
                        {...field}
                      />
                    )}
                  />
                  {errors.image && (
                    <p className="px-1 text-xs text-red-600">
                      {errors.image.message}
                    </p>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button type="submit" className="mt-4 w-full">
                Submit
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}
